import { view } from "@risingstack/react-easy-state";
import { useEffect } from "react";
import { sendFilter } from "../../common/tracking";
import type { FilterSettings, LegalFilterKey, Product } from "../../common/types/productTypes";
import productStore from "../../stores/product/productStore";
import { buildFilter, getRelevantFilterSettings } from "../../stores/product/productUtils";
import theme from "../../themes/theme";
import FilterSelectionOptions from "./FilterSelectionOptions";
import FilterSelectionOptionsMobile from "./FilterSelectionOptionsMobile";

type FilterSelectorsProps = {
   products?: Product[];
   skus?: string[];
   variant?: string;
};

const FilterSelectors = ({ products, skus = [], variant = "" }: FilterSelectorsProps) => {
   const filterAttributesFunction = theme.filterAttributes;
   const resolvedProducts = products ? products : productStore.resolveSkus(skus);
   const filterAttributes = filterAttributesFunction(variant);
   const allFilters: FilterSettings[] = [];
   const filterKeys = Object.keys(filterAttributes) as LegalFilterKey[];
   for (let i = 0; i < filterKeys.length; i++) {
      const fieldKey = filterKeys[i];
      const fieldName = filterAttributes[fieldKey];

      if (!fieldName) {
         continue;
      }

      const filter = buildFilter(resolvedProducts, fieldKey, fieldName);
      if (filter) {
         allFilters.push(filter);
      }
   }

   const relevantFilters = getRelevantFilterSettings(allFilters, productStore.activeFilters, resolvedProducts);

   useEffect(() => {
      for (const filter of relevantFilters) {
         const fieldKey = filter.field;
         // a radio button filter must have a value.  If there is no active filter, we set it here
         if (filter.radioDefault && Object.keys(productStore.activeFilters[fieldKey] || {}).length === 0) {
            productStore.addFilterValue(fieldKey, filter.radioDefault);
         }
      }
      for (const key of Object.keys(productStore.activeFilters)) {
         const activeFilter = productStore.activeFilters[key as LegalFilterKey];
         if (activeFilter) {
            const filter = relevantFilters.find((filter) => filter.field === key);
            if (filter) {
               const optionValues = Object.keys(filter.options);
               if (optionValues) {
                  for (const filterValue of activeFilter) {
                     if (!optionValues.includes(filterValue)) {
                        productStore.removeFilterValue(filter.field, filterValue);
                     }
                  }
               }
            }
         }
      }
   }, [relevantFilters]);

   const filterModified = (e: React.ChangeEvent<HTMLInputElement>, field: LegalFilterKey, value: string, isRadio: boolean) => {
      if (isRadio) {
         productStore.clearFilterFields([field]);
         productStore.addFilterValue(field, value);
         sendFilter(field, value);
         return;
      }
      if (e.target.checked) {
         productStore.addFilterValue(field, value);
         sendFilter(field, value);
      } else {
         productStore.removeFilterValue(field, value);
      }
   };
   return (
      <>
         <div className="tw-hidden sm:tw-block">
            <div className="tw-flex tw-flex-wrap tw-gap-2">
               <FilterSelectionOptions filters={relevantFilters} onFilterChange={filterModified} />
            </div>
         </div>
         <div className="sm:tw-hidden">
            <FilterSelectionOptionsMobile products={products || []} filters={relevantFilters} onFilterChange={filterModified} />
         </div>
      </>
   );
};

export default view(FilterSelectors);
